<template>
  <div class="flex flex-col gap-4 p-4">
    <!--region facturation-->
    <PageSection :title="$t('attributes.format')">
      <div class="grid-form">
        <div>
          <SelectComponent
            v-model="funeralHome.settings.lastname_format"
            :label="
              $t('attributes.last_name') + '(' + $t('global.choose_case') + ')'
            "
            name="lastname_format"
            :values="lastnameFormatValues"
            :edition-mode="editionMode"
            :capitalize="false"
            required
          />
        </div>
        <div>
          <SelectComponent
            v-model="funeralHome.settings.firstname_format"
            :label="
              $t('attributes.first_name') + '(' + $t('global.choose_case') + ')'
            "
            name="firstname_format"
            :values="firstnameFormatValues"
            :edition-mode="editionMode"
            :capitalize="false"
            required
          />
        </div>
        <div>
          <SelectComponent
            v-model="funeralHome.settings.usualname_format"
            :label="
              $t('attributes.usual_first_name') +
              '(' +
              $t('global.choose_case') +
              ')'
            "
            name="usualname_format"
            :values="usualnameFormatValues"
            :edition-mode="editionMode"
            :capitalize="false"
            required
          />
        </div>
        <div class="col-start-1">
          <SelectComponent
            v-model="funeralHome.settings.fullname_order"
            :label="$t('attributes.format')"
            name="fullname_order"
            :values="fullnameOrderValues"
            :edition-mode="editionMode"
            :capitalize="false"
            required
          />
        </div>
        <div>
          <SelectComponent
            v-model="notificationDeceasedNameOrder"
            :label="$t('attributes.mail_subject_deceased_name_order')"
            name="fullname_order_by_context_notification_deceased"
            :values="fullnameOrderValues"
            :none-selected-label="$t('global.full_name_by_default')"
            :edition-mode="editionMode"
            :capitalize="false"
          />
        </div>
      </div>
    </PageSection>
    <!--endregion-->

    <!--region catalogue articles-->
    <PageSection :title="$t('attributes.items')">
      <div class="flex flex-col gap-3">
        <WalkmanButton
          v-model="funeralHome.settings.item_category_custom_order"
          :label="$t('enums.settings.settingsKeys.item_category_custom_order')"
          :options="[
            {
              value: true,
              label: $t('enums.document.documentOrderType.MANUAL')
            },
            {
              value: false,
              label: $t('enums.document.documentOrderType.NONE')
            }
          ]"
          :edition-mode="editionMode"
        />
      </div>
    </PageSection>
    <!--endregion-->

    <!--region dossier-->
    <PageSection :title="$t('global.dossier')">
      <div class="grid-form">
        <div>
          <WalkmanButton
            v-model="funeralHome.settings.show_co_handler"
            :label="$t('global.show_co_handler')"
            :options="[
              {
                value: true,
                label: $t('global.yes')
              },
              {
                value: false,
                label: $t('global.no')
              }
            ]"
            :edition-mode="editionMode"
          />
          <p class="mt-1 text-xs text-gray-400">
            {{ $t('global.show_co_handler_hint') }}
          </p>
        </div>
        <div class="col-start-1">
          <WalkmanButton
            v-model="funeralHome.settings.quick_dossier_creation_enabled"
            :label="$t('attributes.quick_dossier_creation_enabled')"
            :options="[
              {
                value: true,
                label: $t('global.yes')
              },
              {
                value: false,
                label: $t('global.no')
              }
            ]"
            :edition-mode="editionMode"
          />
        </div>
        <div class="col-start-1">
          <WalkmanButton
            v-model="funeralHome.settings.fh_menu_order"
            :label="$t('global.menu_order')"
            :options="menuOrder"
            :edition-mode="editionMode"
          />
        </div>
        <div>
          <SelectComponent
            v-if="funeralHome.settings.fh_menu_order === 'SPECIFIC'"
            v-model="funeralHome.settings.fh_menu_order_country"
            :label="$t('global.fh_menu_order_country')"
            name="fh_menu_order_country"
            :values="menuCountries"
            :edition-mode="editionMode"
            :capitalize="false"
            :multiple="false"
            :searchable="true"
            required
          />
        </div>
      </div>
    </PageSection>
    <!--endregion-->

    <!--region succession-->
    <PageSection :title="$t('global.invoicing')">
      <div class="grid-form grid">
        <div class="col-span-1">
          <BaseShowEditTranslation
            v-model:translations="funeralHome.settings.estate_sentence"
            :label="$t('attributes.estate_sentence')"
            :edition-mode="editionMode"
            name="estate_sentence"
          />
          <p class="mt-1 text-xs text-gray-400">
            {{ $t('hint.estate_sentence_hint') }}
          </p>
        </div>
        <!-- Child estate sentence -->
        <div class="col-span-1">
          <BaseShowEditTranslation
            v-model:translations="funeralHome.settings.child_estate_sentence"
            :label="$t('attributes.child_estate_sentence')"
            :edition-mode="editionMode"
            name="child_estate_sentence"
          />
          <p class="mt-1 text-xs text-gray-400">
            {{ $t('hint.child_estate_sentence_hint') }}
          </p>
        </div>
        <!-- Estate recipient prefix (e.g. C/O) -->
        <div class="col-span-1">
          <BaseShowEditTranslation
            v-model:translations="funeralHome.settings.estate_recipient_prefix"
            :label="$t('attributes.estate_recipient_prefix')"
            :edition-mode="editionMode"
            name="estate_recipient_prefix"
          />
          <p class="mt-1 text-xs text-gray-400">
            {{ $t('hint.estate_recipient_prefix_hint') }}
          </p>
        </div>
      </div>
    </PageSection>
    <!--endregion-->

    <!--region écran-->
    <PageSection :title="$t('global.screen')">
      <ScreenConfigSection
        title="global.hallway"
        screen-key="hallway"
        :config="funeralHome.settings.screen_config.hallway"
        :edition-mode="editionMode"
        :templates="templates"
        :picture-options="pictureOptions"
        :background-options="backgroundOptions"
        :sorted-videos="sortedVideos"
        :emblems="emblems"
      />

      <ScreenConfigSection
        title="global.chamber"
        screen-key="chamber"
        :config="funeralHome.settings.screen_config.chamber"
        :edition-mode="editionMode"
        :templates="templates"
        :picture-options="pictureOptions"
        :background-options="backgroundOptions"
        :sorted-videos="sortedVideos"
        :emblems="emblems"
      />

      <ScreenConfigSection
        title="global.window"
        screen-key="dossier_window"
        :config="funeralHome.settings.screen_config.dossier_window"
        :edition-mode="editionMode"
        :picture-options="pictureOptions"
        :background-options="backgroundOptions"
        :sorted-videos="sortedVideos"
      />
    </PageSection>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import PageSection from '@c/PageSection.vue'
import SelectComponent from '@c/BaseFormComponent/SelectComponent.vue'
import BaseShowEditTranslation from '@c/addf-package/components/BaseShowEditInput/BaseShowEditTranslation.vue'
import WalkmanButton from '@c/WalkmanButton.vue'
import EventType from '@/assets/enums/events/EventType'
import ScreenConfigSection from '@/views/settings/corporation/funeralHome/form/tabs/funeralHomeSettings/components/ScreenConfigSection.vue'
import Country from '@/assets/enums/config/Country.js'
import BaseSettingsMixin from '@/views/settings/general/baseSettingsComponents/mixin/BaseSettingsMixin.js'

export default {
  name: 'TheFuneralHomeSettingsPage',
  components: {
    ScreenConfigSection,
    BaseShowEditTranslation,
    SelectComponent,
    PageSection,
    WalkmanButton
  },
  mixins: [BaseSettingsMixin],
  data() {
    return {
      format: 'ucwords'
    }
  },
  computed: {
    ...mapGetters({
      auth: 'auth/getAuth',
      funeralHome: 'funeralHome/getFuneralHome',
      editionMode: 'funeralHome/getEditionMode',
      templates: 'metadata/getScreenTemplates',
      emblems: 'metadata/getScreenEmblems',
      videos: 'metadata/getVideos'
    }),
    EventType() {
      return EventType
    },
    lastnameFormatValues() {
      return [
        {
          name: this.$h.convertToTitleCase(this.$t('attributes.last_name')),
          id: 'ucwords'
        },
        {
          name: this.$t('attributes.last_name').toUpperCase(),
          id: 'uppercase'
        },
        {
          name: this.$t('attributes.last_name').toLowerCase(),
          id: 'lowercase'
        }
      ]
    },
    firstnameFormatValues() {
      return [
        {
          name: this.$h.convertToTitleCase(this.$t('attributes.first_name')),
          id: 'ucwords'
        },
        {
          name: this.$t('attributes.first_name').toUpperCase(),
          id: 'uppercase'
        },
        {
          name: this.$t('attributes.first_name').toLowerCase(),
          id: 'lowercase'
        }
      ]
    },
    usualnameFormatValues() {
      const surnom = this.$t('attributes.usual_first_name')
      return [
        {
          name: this.$h.convertToTitleCase(
            this.$t('attributes.usual_first_name')
          ),
          id: 'ucwords'
        },
        {
          name: this.$t('attributes.usual_first_name').toUpperCase(),
          id: 'uppercase'
        },
        {
          name: this.$t('attributes.usual_first_name').toLowerCase(),
          id: 'lowercase'
        }
      ]
    },
    /**
     * Deceased name order of the default mail subject and the mail notifications (BBSW-4460).
     * Null keeps the deceased full name, as before the setting existed.
     */
    notificationDeceasedNameOrder: {
      get() {
        return (
          this.funeralHome.settings.fullname_order_by_context?.notification
            ?.deceased ?? null
        )
      },
      set(order) {
        const ordersByContext =
          this.funeralHome.settings.fullname_order_by_context ?? {}

        this.funeralHome.settings.fullname_order_by_context = {
          ...ordersByContext,
          notification: {
            ...ordersByContext.notification,
            deceased: order ?? null
          }
        }
      }
    },
    fullnameOrderValues() {
      const firstname = this.applyCase(
        this.$t('attributes.first_name'),
        this.funeralHome.settings.firstname_format
      )
      const lastname = this.applyCase(
        this.$t('attributes.last_name'),
        this.funeralHome.settings.lastname_format
      )
      const usualname = this.applyCase(
        this.$t('attributes.usual_first_name'),
        this.funeralHome.settings.usualname_format
      )

      return [
        {
          name: `${firstname} + ${lastname}`,
          id: 'firstname_lastname'
        },
        {
          name: `${lastname} + ${firstname}`,
          id: 'lastname_firstname'
        },
        {
          name: `${usualname} + ${lastname}`,
          id: 'usualname_lastname'
        },
        {
          name: `${lastname} + ${usualname}`,
          id: 'lastname_usualname'
        }
      ]
    },
    pictureOptions() {
      return [
        {
          value: 'no',
          label: this.$t('global.without')
        },
        {
          value: 'rounded',
          label: this.$t('global.rounded')
        },
        {
          value: 'square',
          label: this.$t('global.square')
        },
        {
          value: 'rectangle',
          label: this.$t('global.rectangle')
        }
      ]
    },
    backgroundOptions() {
      return [
        {
          value: 'color',
          label: this.$t('global.color')
        },
        {
          value: 'video',
          label: this.$t('global.video')
        },
        {
          value: 'gallery',
          label: this.$t('global.gallery')
        }
      ]
    },
    sortedVideos() {
      return [...this.videos].sort((a, b) => {
        return a.name.localeCompare(b.name)
      })
    },
    menuOrder() {
      return [
        {
          value: 'GLOBAL',
          label: this.$t('global.global_settings')
        },
        {
          value: 'USER',
          label: this.$t('global.menu_order_user')
        },
        {
          value: 'SPECIFIC',
          label: this.$t('global.menu_order_specific')
        }
      ]
    },
    menuCountries() {
      return this.initialMetadata.enums.countries
        .filter((country) =>
          [
            Country.BELGIUM,
            Country.SWITZERLAND,
            Country.NETHERLANDS,
            Country.SPAIN,
            Country.ITALY,
            Country.PORTUGAL,
            Country.GERMANY,
            Country.LUXEMBOURG,
            Country.FRANCE,
            Country.MARTINIQUE
          ].includes(country.id)
        )
        .map((country) => {
          return { id: country.id, name: country.name }
        })
    }
  },
  methods: {
    applyCase(value, format) {
      if (!value) return ''

      switch (format) {
        case 'uppercase':
          return value.toUpperCase()
        case 'lowercase':
          return value.toLowerCase()
        case 'ucwords':
        default:
          return this.$h.convertToTitleCase(value)
      }
    }
  }
}
</script>
